import { Alert, Box, Card, CardHeader, CardContent, useTheme, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Drawer } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { bloqcibeApi, useSaveCommentMutation } from '../../../store/slices/apiSlice';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { setUserLocation } from '../../../store/slices/userDetailsSlice';
import { openMessage } from '../../../store/slices/showMessageSlice';
import { maskString } from '../../../util/util';
import CommentTab from './CommentTab';
import HistoryTab from './HistoryTab';
import QueryTab from './QueryTab';
import { CustomTabPanel, a11yProps } from './TabPanel';

const CommentHistory = (props) => {
  const { trialId, siteId, trialSiteId, subjectMasterId, crfMasterId, visitStepKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    open,
    onCloseHistory,
    field,
    answer,
    sectionKey,
    subSectionKey,
    categoryKey,
    parentKey,
    stepKey,
    MASK_FIELD_KEY,
    userSiteTrialRole,
    formConfig,
    hiddenFieldNotice,
  } = props;
  const resolvedCategoryKey = categoryKey || parentKey || null;

  // Only keep this drawer open when URL targets this field (+ category when applicable)
  const queryFieldKey = searchParams.get('fieldKey');
  const queryCategoryKey = searchParams.get('categoryKey');
  const fieldKeyMatches = !queryFieldKey || queryFieldKey === field?.key;
  const categoryMatches = resolvedCategoryKey
    ? queryCategoryKey === resolvedCategoryKey
    : !queryCategoryKey;
  const isDrawerOpen = Boolean(open && fieldKeyMatches && categoryMatches);

  // Helper function to extract names from formConfig
  const getNamesFromFormConfig = () => {
    if (!formConfig || !formConfig.steps) {
      return { stepName: null, sectionName: null, subSectionName: null, categoryName: null };
    }

    const currentStepKey = visitStepKey || stepKey;
    const step = formConfig.steps.find((s) => s.key === currentStepKey);
    const stepName = step?.label || null;

    let sectionName = null;
    let subSectionName = null;
    let categoryName = null;

    if (step && sectionKey) {
      const section = step.sections?.find((s) => s.key === sectionKey);
      sectionName = section?.label || null;

      if (section && subSectionKey && section.subTabs) {
        const subSection = section.subTabs.find((s) => s.key === subSectionKey);
        subSectionName = subSection?.label || null;
      }

      if (section && resolvedCategoryKey) {
        // Categories can be in sections or subSections
        const category = section.categories?.find((c) => c.key === resolvedCategoryKey) ||
          (subSectionName && section.subTabs?.find((s) => s.key === subSectionKey)?.categories?.find((c) => c.key === resolvedCategoryKey));
        categoryName = category?.label || null;
      }
    }

    return { stepName, sectionName, subSectionName, categoryName };
  };

  const { stepName, sectionName, subSectionName, categoryName } = getNamesFromFormConfig();
  const dispatch = useDispatch();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const trialUsers = useSelector((state) => state.trial.trialUsers);
  const userDetails = useSelector((state) => state.userDetails);
  const loggedInUser = useSelector((state) => state.userDetails.loggedInUser);
  const [getComments] = bloqcibeApi.endpoints.getComments.useLazyQuery();
  const [getFieldHistory] = bloqcibeApi.endpoints.getFieldHistory.useLazyQuery();
  const [getSubjectFieldHistory] = bloqcibeApi.endpoints.getSubjectFieldHistory.useLazyQuery();
  const [saveComment] = useSaveCommentMutation();
  const [users, setUsers] = useState([]);
  const [commentHist, setCommentHist] = useState([]);
  const [fieldHist, setFieldHist] = useState([]);
  const [comment, setComment] = useState('');
  const theme = useTheme();
  
  // Initialize tab from URL query params or default to 0 (Query)
  const getInitialTab = () => {
    if (!isDrawerOpen) return 0; // If drawer is closed, don't initialize
    const tabParam = searchParams.get('tab');
    if (tabParam === 'comment') return 1;
    if (tabParam === 'history') return 2;
    return 0; // Default to Query tab
  };
  const [value, setValue] = useState(getInitialTab());

  // Sync tab with URL query params when drawer opens
  useEffect(() => {
    if (isDrawerOpen) {
      const tabParam = searchParams.get('tab');
      if (tabParam === 'comment') setValue(1);
      else if (tabParam === 'history') setValue(2);
      else if (!tabParam) setValue(0);
    }
  }, [isDrawerOpen, searchParams]);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
    
    // Update URL query params
    const params = new URLSearchParams(searchParams);
    if (newValue === 0) {
      params.set('tab', 'query');
    } else if (newValue === 1) {
      params.set('tab', 'comment');
    } else if (newValue === 2) {
      params.set('tab', 'history');
    }
    setSearchParams(params, { replace: true });
  };
  useEffect(() => {
    if (trialUsers) {
      const _users = trialUsers
        .filter(
          (_user) => _user.siteId === Number(siteId) && _user.role !== 'site_admin'
        )
        .map((_obj) => {
          return {
            id: _obj.user.email,
            display: _obj.user.firstName + ' ' + _obj.user.lastName,
            userId: _obj.user.id,
            self: _obj.userId === loggedInUser?.id,
            role: _obj.role,
          };
        });
      setUsers(_users);
    }
  }, [trialUsers, siteId]);

  useEffect(() => {
    (async () => {
      fetchCommentHistory();
      fetchValueHistory();
    })();
  }, [field]);
  useEffect(() => {
    if (userDetails && (!userDetails.locationLat || !userDetails.locationLng)) {
      getGeoLocation();
    }
  }, [userDetails]);
  const getGeoLocation = async (processSubmit, isOptional = false) => {
    if ('geolocation' in navigator) {
      await navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);
          // Do something with the latitude and longitude
          if (latitude && longitude) {
            dispatch(
              setUserLocation({
                locationLatitude: latitude,
                locationLongitude: longitude,
              })
            );
          }
          processSubmit && processSubmit({ locationLat: latitude, locationLng: longitude });
        },
        (error) => {
          if (!isOptional) {
            // dispatch(
            //   openMessage({
            //     message: "Please enable location access to add the comment.",
            //     messageSeverity: "error",
            //   })
            // );
          }
          console.error(`Error getting geolocation: ${error.message}`);
          // If optional and processSubmit is provided, proceed without location
          if (isOptional && processSubmit) {
            processSubmit({ locationLat: null, locationLng: null });
          }
        }
      );
    } else {
      // Geolocation is not supported
      if (!isOptional) {
        dispatch(
          openMessage({
            message: 'Geolocation is not supported!',
            messageSeverity: 'error',
          })
        );
      }
      console.log('Geolocation is not supported');
      // If optional and processSubmit is provided, proceed without location
      if (isOptional && processSubmit) {
        processSubmit({ locationLat: null, locationLng: null });
      }
    }
  };
  const fetchValueHistory = async () => {
    const payload = {
      ...(visitStepKey ? { crfMasterId: crfMasterId } : { subjectMasterId: subjectMasterId }),
      stepKey: visitStepKey ? visitStepKey : stepKey,
      sectionKey: sectionKey,
      subSectionKey: subSectionKey,
      categoryKey: resolvedCategoryKey,
      fieldKey: field.key,
    };
    let valueHis;
    if (visitStepKey) {
      valueHis = await getFieldHistory({
        payload,
      });
    } else {
      valueHis = await getSubjectFieldHistory({
        payload,
      });
    }

    const _valuesList = valueHis.data.map((_obj) => {
      return {
        user: _obj.createdBy,
        value:
          MASK_FIELD_KEY.includes(_obj.fieldKey) && (userSiteTrialRole === 'site_monitor' || userSiteTrialRole === 'data_manager')
            ? maskString(_obj.fieldValue)
            : _obj.fieldValue,
        date: moment(_obj.Timestamp).format('MM/DD/YYYY hh:mm a'),
      };
    });
    setFieldHist(_valuesList);
  };
  const fetchCommentHistory = async () => {
    const commentHis = await getComments({
      sponsorId: sponsorId,
      trialId: trialId,
      trialSiteId: siteId, //trialSiteData?.siteTrialData?.site?.id,
      subjectMasterId: subjectMasterId,
      payload: {
        crfMasterId: crfMasterId,
        stepKey: visitStepKey ? visitStepKey : stepKey,
        sectionKey: sectionKey,
        subSectionKey: subSectionKey,
        categoryKey: resolvedCategoryKey,
        fieldKey: field.key,
      },
    });
    const _commentList = commentHis.data.map((_obj) => {
      let comm = _obj.comment;
      const matches = comm.match(/\@\[(.*?)\]\([^)]+\)/g);
      if (matches) {
        for (let i = 0; i < matches?.length; i++) {
          const match = matches[i].match(/\@\[(.*?)\]\((.*?)\)/);
          if (match) {
            const username = match[1];
            const userEmail = match[2];
            comm = comm.replace(
              match[0],
              `<span style='background-color: #e8edff; font-style: italic;'>${username}</span>`
            );
          }
        }
      }
      return {
        ..._obj,
        comment: comm,
      };
    });
    setCommentHist(_commentList);
  };
  const handleChange = (event, newValue, newPlainTextValue, mentions) => {
    setComment(newValue);
  };
  const saveCommentData = async () => {
    let _locationObj = { ...userDetails };
    // if (!_locationObj.locationLat || !_locationObj.locationLng) {
    //   // Try to get location, but proceed even if it fails (location is optional)
    //   await getGeoLocation(processSubmit, true);
    // } else {
      await processSubmit(_locationObj);
    // }
  };
  const processSubmit = async (_locationObj) => {
    if (!comment.trim()) {
      return;
    }
    //console.log('value', comment)
    const payload = {
      sponsorId: sponsorId,
      trialId: trialId,
      siteId: siteId,
      trialSiteId: trialSiteId,
      subjectMasterId: subjectMasterId,
      crfMasterId: crfMasterId,
      stepKey: visitStepKey ? visitStepKey : stepKey,
      sectionKey: sectionKey,
      subSectionKey: subSectionKey,
      categoryKey: resolvedCategoryKey,
      fieldKey: field.key,
      comment: comment,
      ...(_locationObj?.locationLat &&
        _locationObj?.locationLng && {
        latitude: _locationObj.locationLat,
        longitude: _locationObj.locationLng,
      }),
      notificationData: {
        fieldLabel: field?.label,
        subjectName: props?.subjectName,
        formLabel: props?.formLabel,
      },
    };
    await saveComment({ payload });
    setComment('');
    fetchCommentHistory();
  };

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={onCloseHistory}
      sx={{
        width: 500, // Set the width of the Drawer
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 500, // Set the width of the Drawer paper
          height: '100%',
          boxSizing: 'border-box',
        },
      }}
    >
      <Box
        sx={{
          paddingTop: 8,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Card sx={{ flexGrow: 1, py:0, height: '100%' }}>
          <Box
            sx={{
              width: '100%',
              height: 4,
              backgroundColor: theme.palette.primary.main,
            }}
          />
          <CardHeader
            title={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="span"
                  title={field.label}
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    // flexGrow: 1,
                    minWidth: 0,
                    maxWidth: 'calc(70% - 10px)',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    noWrap
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    {field.label}
                  </Typography>
                </Box>
                <Box
                  component="span"
                  sx={{
                    marginLeft: 0.5,
                    color: theme.palette.text.secondary,
                    flexShrink: 0,
                    minWidth: 0,
                    maxWidth: '30%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={['Query', 'Comment', 'History'][value]}
                >
                  ({['Query', 'Comment', 'History'][value]})
                </Box>
              </Box>
            }
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
            sx={{ backgroundColor: theme.palette.grey[50], minWidth: 0, overflow: 'hidden' }}
          />
          <CardContent sx={{ height: '100%', pt:0, }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Box sx={{ flexShrink: 0 }}>
                  <Tabs value={value} onChange={handleTabChange}>
                    <Tab label="Query" {...a11yProps(0)} />
                    <Tab label="Comment" {...a11yProps(1)} />
                    <Tab label="History" {...a11yProps(2)} />
                  </Tabs>
                </Box>
                <Box sx={{ flexGrow: 1, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  {hiddenFieldNotice && (
                    <Alert severity="warning" sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
                      {hiddenFieldNotice}
                    </Alert>
                  )}
                  <CustomTabPanel
                    value={value}
                    index={0}
                    sx={{ height: '95%', display: 'flex', flexDirection: 'column' }}
                  >
                    <QueryTab
                      sponsorId={sponsorId}
                      trialId={trialId}
                      siteId={siteId}
                      trialSiteId={trialSiteId}
                      subjectMasterId={subjectMasterId}
                      crfMasterId={crfMasterId}
                      stepKey={visitStepKey ? visitStepKey : stepKey}
                      sectionKey={sectionKey}
                      subSectionKey={subSectionKey}
                      categoryKey={resolvedCategoryKey}
                      fieldKey={field?.key}
                      fieldLabel={field?.label}
                      stepName={stepName}
                      sectionName={sectionName}
                      subSectionName={subSectionName}
                      categoryName={categoryName}
                      users={users}
                      userSiteTrialRole={userSiteTrialRole}
                    />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={1}>
                    <CommentTab
                      commentHist={commentHist}
                      comment={comment}
                      users={users}
                      onCommentChange={handleChange}
                      onSaveComment={saveCommentData}
                    />
                  </CustomTabPanel>
                  <CustomTabPanel value={value} index={2}>
                    <HistoryTab fieldHist={fieldHist} />
                  </CustomTabPanel>
                            </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Drawer>
  );
};

export default React.memo(CommentHistory);
